import React, { useState, useEffect } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Button, TextField, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, InputAdornment, Grid2, Box, Typography, useTheme, Card, CardContent, IconButton, Tooltip, Chip } from '@mui/material';
import { Add, Search, Event, LocationOn, AccessTime, Category, Notes, Edit, Delete } from '@mui/icons-material';
import { getEventActivities, createActivity, updateActivity, deleteActivity } from '../actions/activities';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns-tz';
import AccessDenied from '../components/AccessDenied';
import { useAlert } from '../contexts/AlertContext';
import { cardGridSx, cardItemSx } from '../utils/cardLayout';

const localizer = momentLocalizer(moment);

const CalendarIntegration = () => {
  const theme = useTheme();
  const { showAlert } = useAlert();
  const { eventId } = useParams(); // Get eventId from URL params
  const [activities, setActivities] = useState([]);
  const [currentActivity, setCurrentActivity] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [activityToDelete, setActivityToDelete] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  
  // Get user's time zone
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Fetch activities from API on load
  useEffect(() => {
    const fetchData = async () => {
      try {
        const fetchedActivities = await getEventActivities(eventId);

        const formattedActivities = fetchedActivities.map(activity => {        
          const startDate = new Date(activity.start);
          const endDate = new Date(activity.end);
        
          return {
            ...activity, // You may need to remove toJSON if it's not a model instance
            start: startDate,
            end: endDate,
          };
        });
        
        setActivities(formattedActivities);
      } catch (error) {
        if (error?.response?.status === 403) {
          setAccessDenied(true);
        } else {
          showAlert(error?.response?.data?.error || error?.response?.data?.message || error.message || 'Failed to load data', 'error');
        }
      }
    };
    fetchData();
  }, [eventId, timeZone]); // Include timeZone as a dependency

  // Open dialog for creating/editing
  const handleOpenDialog = (activity = {}) => {
    setCurrentActivity(activity);
    setIsEditing(!!activity.id);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setCurrentActivity({});
    setOpenDialog(false);
  };

  const handleActivityChange = (e) => {
    const { name, value } = e.target;
    setCurrentActivity({ ...currentActivity, [name]: value });
  };

  const handleSaveActivity = async () => {
    try {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  
      const activityData = {
        ...currentActivity,
        timeZone,
      };
  
      if (isEditing) {
        await updateActivity(currentActivity.id, activityData);
      } else {
        await createActivity(eventId, activityData);
      }
  
      const updatedActivities = await getEventActivities(eventId);
      const formattedActivities = updatedActivities.map(activity => {        
        const startDate = new Date(activity.start);
        const endDate = new Date(activity.end);
      
        return {
          ...activity,
          start: startDate,
          end: endDate,
        };
      });
      setActivities(formattedActivities);
      handleCloseDialog();
    } catch (error) {
      showAlert(error?.response?.data?.error || error?.response?.data?.message || error.message || 'Operation failed', 'error');
      console.error('Failed to save activity', error);
    }
  };

  const handleDeleteActivity = (activityId) => {
    setActivityToDelete(activityId);
    setOpenDeleteDialog(true);
  };

  const confirmDeleteActivity = async () => {
    try {
      await deleteActivity(activityToDelete);
      const updatedActivities = await getEventActivities(eventId); // Refresh after delete
      const formattedActivities = updatedActivities.map(activity => {        
        const startDate = new Date(activity.start);
        const endDate = new Date(activity.end);
      
        return {
          ...activity,
          start: startDate,
          end: endDate,
        };
      });
      setActivities(formattedActivities);
      setOpenDeleteDialog(false);
      handleCloseDialog();
    } catch (error) {
      showAlert(error?.response?.data?.error || error?.response?.data?.message || error.message || 'Operation failed', 'error');
      console.error('Failed to delete activity', error);
    }
  };

  const cancelDeleteActivity = () => {
    setOpenDeleteDialog(false);
    setActivityToDelete(null);
  };

  const filteredActivities = activities.filter((activity) => {
    const matchesSearch = activity.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTag = filterTag ? activity.tags.includes(filterTag) : true;
    return matchesSearch && matchesTag;
  });

  const uniqueTags = Array.from(new Set(activities.flatMap(activity => activity.tags)));

  const formatDateTime = (date) => {
    if (!date) return 'Not set';
    return format(date, 'MMM dd, yyyy - HH:mm', { timeZone });
  };

  if (accessDenied) return <AccessDenied />;

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Controls Section */}
      <Box sx={{ mb: 4, mt: { xs: 4, sm: 0 } }}>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<Add />} 
              onClick={() => handleOpenDialog()} 
              fullWidth 
              sx={{ 
                height: '56px',
                borderRadius: 2,
                fontSize: { xs: '0.9rem', sm: '1rem' },
                fontWeight: 600,
                '&:hover': {
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Add New Activity
            </Button>
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField 
              label="Filter by Tag" 
              select 
              value={filterTag} 
              onChange={(e) => setFilterTag(e.target.value)} 
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            >
              <MenuItem value="">All Tags</MenuItem>
              {uniqueTags.map(tag => (
                <MenuItem key={tag} value={tag}>{tag}</MenuItem>
              ))}
            </TextField>
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 12, md: 5 }}>
            <TextField 
              label="Search Activity" 
              value={searchQuery} 
              onChange={(e) => setSearchQuery(e.target.value)} 
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }
              }}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
          </Grid2>
        </Grid2>
        
        {/* Empty State */}
        {filteredActivities.length === 0 && (
          <Card sx={{ 
            p: { xs: 4, sm: 6 }, 
            textAlign: 'center',
            mt: 3,
            background: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.05)'
              : 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 4px 20px rgba(0, 0, 0, 0.2)'
              : '0 4px 20px rgba(0, 0, 0, 0.1)',
            border: theme.palette.mode === 'dark'
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(255, 255, 255, 0.2)',
          }}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              gap: 2
            }}>
              <Event sx={{ 
                fontSize: 64, 
                color: theme.palette.primary.main,
                opacity: 0.7
              }} />
              <Box>
                <Typography variant="h5" sx={{ 
                  fontWeight: 600, 
                  color: theme.palette.text.primary,
                  mb: 1,
                  fontSize: { xs: '1.25rem', sm: '1.5rem' }
                }}>
                  {(searchQuery || filterTag) ? 
                    'No activities found for the selected filters' : 
                    'No activities added yet'
                  }
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ 
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  lineHeight: 1.5
                }}>
                  {(searchQuery || filterTag) ? 
                    'Try adjusting your filters to see more results.' : 
                    'Start scheduling your event activities by clicking "Add New Activity" above.'
                  }
                </Typography>
              </Box>
            </Box>
          </Card>
        )}
      </Box>

      {/* Calendar Container */}
      <Card sx={{
        background: theme.palette.mode === 'dark' 
          ? 'rgba(255, 255, 255, 0.05)'
          : 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(10px)',
        borderRadius: 3,
        boxShadow: theme.palette.mode === 'dark'
          ? '0 4px 20px rgba(0, 0, 0, 0.2)'
          : '0 4px 20px rgba(0, 0, 0, 0.1)',
        border: theme.palette.mode === 'dark'
          ? '1px solid rgba(255, 255, 255, 0.1)'
          : '1px solid rgba(255, 255, 255, 0.2)',
        p: { xs: 1, sm: 2 },
        overflow: 'hidden',
        mb: 4
      }}>
        <Calendar
          localizer={localizer}
          events={filteredActivities}
          startAccessor="start"
          endAccessor="end"
          style={{ 
            height: 500,
            fontFamily: theme.typography.fontFamily
          }}
          onSelectEvent={handleOpenDialog}
        />
      </Card>

      {/* Activities List */}
      {filteredActivities.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            All Activities
          </Typography>
          <Box sx={cardGridSx()}>
            {filteredActivities.map((activity, index) => (
              <Box key={activity.id || index} sx={cardItemSx()}>
                <Card sx={{
                  height: '100%',
                  borderRadius: 3,
                  background: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 4px 20px rgba(0, 0, 0, 0.2)'
                    : '0 4px 20px rgba(0, 0, 0, 0.1)',
                  border: theme.palette.mode === 'dark'
                    ? '1px solid rgba(255, 255, 255, 0.1)'
                    : '1px solid rgba(255, 255, 255, 0.2)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.palette.mode === 'dark'
                      ? '0 8px 30px rgba(0, 0, 0, 0.3)'
                      : '0 8px 30px rgba(0, 0, 0, 0.15)',
                  }
                }}>
                  <CardContent sx={{ p: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, minHeight: '1.5rem' }}>
                      {activity.title}
                    </Typography>

                    {/* Activity Details Section */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <AccessTime sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: theme.palette.primary.main }} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                          {formatDateTime(activity.start)}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <AccessTime sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: theme.palette.secondary.main }} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                          {formatDateTime(activity.end)}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <LocationOn sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: theme.palette.primary.main }} />
                        <Typography variant="body2" color="text.secondary" sx={{ 
                          fontSize: { xs: '0.8rem', sm: '0.875rem' },
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {activity.location || 'No location'}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Tags Section */}
                    {activity.tags && activity.tags.length > 0 && (
                      <Box sx={{ mb: 2, minHeight: '2rem' }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {activity.tags.slice(0, 3).map((tag, tagIndex) => (
                            <Chip
                              key={tagIndex}
                              label={tag}
                              size="small"
                              sx={{
                                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                height: { xs: 20, sm: 24 },
                                backgroundColor: theme.palette.primary.main + '20',
                                color: theme.palette.primary.main,
                                '& .MuiChip-label': {
                                  px: { xs: 1, sm: 1.5 }
                                }
                              }}
                            />
                          ))}
                          {activity.tags.length > 3 && (
                            <Chip
                              label={`+${activity.tags.length - 3}`}
                              size="small"
                              sx={{
                                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                height: { xs: 20, sm: 24 },
                                backgroundColor: theme.palette.grey[300],
                                color: theme.palette.text.secondary,
                                '& .MuiChip-label': {
                                  px: { xs: 1, sm: 1.5 }
                                }
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    )}

                    {/* Description Section - Fixed height */}
                    <Box sx={{ mb: 2, minHeight: '2.5rem' }}>
                      {activity.description && (
                        <Tooltip 
                          title={activity.description} 
                          arrow 
                          placement="top"
                          sx={{ 
                            '& .MuiTooltip-tooltip': {
                              maxWidth: 300,
                              fontSize: '0.875rem',
                              lineHeight: 1.4
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', cursor: 'help' }}>
                            <Notes sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, mt: 0.2, color: theme.palette.primary.main }} />
                            <Typography variant="body2" color="text.secondary" sx={{ 
                              fontSize: { xs: '0.8rem', sm: '0.875rem' }, 
                              fontStyle: 'italic',
                              overflow: 'hidden',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical'
                            }}>
                              {activity.description}
                            </Typography>
                          </Box>
                        </Tooltip>
                      )}
                    </Box>

                    {/* Action buttons - Always at bottom */}
                    <Box sx={{ 
                      mt: 'auto',
                      display: 'flex', 
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      pt: 2,
                      borderTop: `1px solid ${theme.palette.divider}`,
                      gap: 0.5
                    }}>
                      <Tooltip title="Edit Activity" arrow>
                        <IconButton 
                          size="small"
                          onClick={() => handleOpenDialog(activity)}
                          sx={{ 
                            color: theme.palette.primary.main,
                            '&:hover': { backgroundColor: theme.palette.primary.main + '20' }
                          }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Activity" arrow>
                        <IconButton 
                          size="small"
                          onClick={() => handleDeleteActivity(activity.id)}
                          sx={{ 
                            color: theme.palette.error.main,
                            '&:hover': { backgroundColor: theme.palette.error.main + '20' }
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Activity Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="md">
        <DialogTitle sx={{ 
          fontWeight: 600,
          background: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(255, 255, 255, 0.9)',
        }}>
          {isEditing ? 'Edit Activity' : 'Add New Activity'}
        </DialogTitle>
        <DialogContent sx={{ 
          background: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.02)'
            : 'rgba(255, 255, 255, 0.9)',
          pt: 2
        }}>
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <TextField 
                label="Activity Title" 
                name="title" 
                value={currentActivity.title || ''} 
                onChange={handleActivityChange} 
                fullWidth 
                margin="normal"
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <TextField 
                label="Location" 
                name="location" 
                value={currentActivity.location || ''} 
                onChange={handleActivityChange} 
                fullWidth 
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid2>
            <Grid2 size={{ xs: 12 }}>
              <TextField 
                label="Description" 
                name="description" 
                value={currentActivity.description || ''} 
                onChange={handleActivityChange} 
                fullWidth 
                margin="normal"
                multiline
                rows={3}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid2>
            <Grid2 size={{ xs: 12 }}>
              <TextField 
                label="Tags" 
                name="tags" 
                value={currentActivity.tags ? currentActivity.tags.join(', ') : ''} 
                onChange={(e) => handleActivityChange({ target: { name: 'tags', value: e.target.value.split(',').map(tag => tag.trim()) } })} 
                fullWidth 
                margin="normal" 
                placeholder="Enter tags separated by commas"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <TextField 
                label="Start Time" 
                type="datetime-local" 
                name="start" 
                value={currentActivity.start ? format(currentActivity.start, 'yyyy-MM-dd\'T\'HH:mm', { timeZone }) : ''} 
                onChange={handleActivityChange} 
                fullWidth 
                margin="normal" 
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <TextField 
                label="End Time" 
                type="datetime-local" 
                name="end" 
                value={currentActivity.end ? format(currentActivity.end, 'yyyy-MM-dd\'T\'HH:mm', { timeZone }) : ''} 
                onChange={handleActivityChange} 
                fullWidth 
                margin="normal" 
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid2>
          </Grid2>
        </DialogContent>
        <DialogActions sx={{ 
          background: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(255, 255, 255, 0.9)',
          p: { xs: 1, sm: 2 },
          gap: { xs: 1, sm: 0 },
          flexDirection: 'row',
          alignItems: 'center'
        }}>
          {isEditing && (
            <Button 
              color="error" 
              onClick={() => handleDeleteActivity(currentActivity.id)}
              sx={{ borderRadius: 2 }}
            >
              Delete
            </Button>
          )}
          <Button 
            onClick={handleCloseDialog}
            color="error"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleSaveActivity}
            sx={{ borderRadius: 2 }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={cancelDeleteActivity} maxWidth="sm">
        <DialogTitle sx={{ 
          fontWeight: 600,
          background: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(255, 255, 255, 0.9)',
        }}>
          Delete Activity
        </DialogTitle>
        <DialogContent sx={{ 
          background: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.02)'
            : 'rgba(255, 255, 255, 0.9)',
          pt: 2
        }}>
          <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
            Are you sure you want to delete this activity? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ 
          background: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(255, 255, 255, 0.9)',
          p: { xs: 1, sm: 2 },
          gap: { xs: 1, sm: 0 },
          flexDirection: 'row',
          alignItems: 'center'
        }}>
          <Button 
            onClick={cancelDeleteActivity} 
            color="primary"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDeleteActivity} 
            color="error"
            variant="contained"
            sx={{ borderRadius: 2 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CalendarIntegration;
